/**
 * A round mark in a tone: its drawing in the tone's ink on a wash of the tone
 * (tokens.css's --mark-wash-*), so the tone colours the mark alone and the
 * words beside it keep their own inks. A toast's beside its title, a notice's
 * beside its title, the head of a way in's card, which a notice sets the same
 * way, and the team all done beside the wait for the other teams. Toast,
 * NoticeSheet, the entry's card and the wait each drew it with a tone table
 * or a size of their own; this is the one mark.
 *
 *   accent   the player's own — the session's end, a moderator asking for them — and a toast's information
 *   success  something done right: a toast's check, the team all done
 *   warning  look again: a toast's warning
 *   danger   something stands in the way: the connection lost, a removal, a failure
 *   neutral  the label ink on the hairline wash: it only asks, or only says — the full screen a session wants, the landing's lock
 *
 * `size` sm is a toast's 24px and lg a notice's 36px, and the mark draws its
 * glyph at its own size — sm in a sm mark, md in a lg one — so no caller sets
 * it. A drawing laid on its side is drawn so, its turn its own: the plane mark
 * on the session's end (common/plane-glyph.ts's PLANE_MARK). It is decoration,
 * hidden from assistive technology: the title beside it says what it is
 * about.
 *
 *   <ToneMark tone="danger" size="lg" glyph={GLYPH.exclamation} />
 */

import type { Drawing } from './glyphs';
import { Icon } from './icon';
import styles from './tone-mark.module.css';

const TONE = {
  accent: styles.accent,
  success: styles.success,
  warning: styles.warning,
  danger: styles.danger,
  neutral: styles.neutral,
} as const;

/** Each size's sheet class, and the size its glyph is drawn at. */
const SIZE = {
  sm: { className: styles.sm, glyph: 'sm' },
  lg: { className: styles.lg, glyph: 'md' },
} as const;

interface ToneMarkProps {
  tone: keyof typeof TONE;
  /** sm 24px, a toast's; lg 36px, a notice's. */
  size?: keyof typeof SIZE;
  /** Its drawing, drawn at the mark's own size for it, laid on its side if it is (turned()). */
  glyph: Drawing;
}

export function ToneMark({ tone, size = 'sm', glyph }: ToneMarkProps) {
  return (
    <span className={[styles.mark, TONE[tone], SIZE[size].className].join(' ')} aria-hidden="true">
      <Icon glyph={glyph} size={SIZE[size].glyph} />
    </span>
  );
}
